import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getYardsByCompanyId,
  getReportStats,
  getGatesByYardIds,
  getGateStatusOverview,
  getWarehouseCargoSummary,
  getHourlyThroughput,
  getLatestSurveyDate,
} from "../../pages/Home/api";
import { formatDateToISOString } from "../../utils/utils";
// NOTE: adjust this import path to wherever filterSlice actually lives
import { setFilters } from "./filterSlice";

const getLocalDateString = (d = new Date()) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

// last 7 days ending on a given anchor date (defaults to today)
const getDefaultDates = (anchor = new Date()) => {
  const start = new Date(anchor);
  start.setDate(anchor.getDate() - 6);
  return {
    startDate: getLocalDateString(start),
    endDate: getLocalDateString(anchor),
  };
};

const isDateStale = (date) => !!date && date < getLocalDateString();

// ── YARD ──────────────────────────────────────────────────────
export const fetchYardHomeData = createAsyncThunk(
  "home/fetchYardHomeData",
  async ({ companyId, filters }, { dispatch, rejectWithValue }) => {
    try {
      const res = await getYardsByCompanyId(companyId, "YARD");
      if (!res.status || !Array.isArray(res.data))
        throw new Error("Failed to fetch yards");

      const yardOptions = res.data.map((yard) => ({
        label: yard.yard_name,
        value: yard.yard_id,
      }));
      const allYardIds = res.data.map((yard) => yard.yard_id);

      let anchorDate = new Date();
      try {
        const surveyRes = await getLatestSurveyDate(allYardIds);
        const latestSurveyDate = surveyRes?.data?.latest_survey_date;
        if (latestSurveyDate) anchorDate = new Date(latestSurveyDate);
      } catch (surveyErr) {
        console.error("Error fetching latest survey date:", surveyErr);
      }

      const defaultDates = getDefaultDates(anchorDate);

      if (!filters.startDate || !filters.endDate) {
        dispatch(
          setFilters({
            ...filters,
            startDate: filters.startDate || defaultDates.startDate,
            endDate: filters.endDate || defaultDates.endDate,
          }),
        );
      }

      const safeStartDate = filters.startDate || defaultDates.startDate;
      const safeEndDate = isDateStale(filters.endDate)
        ? defaultDates.endDate
        : filters.endDate || defaultDates.endDate;

      const payload = {
        yardIds: filters.yards?.length > 0 ? filters.yards : allYardIds,
        startDate: formatDateToISOString(safeStartDate, true),
        endDate: formatDateToISOString(safeEndDate, false),
      };

      const reportData = await getReportStats(payload);

      return {
        yardOptions,
        allYardIds,
        reportStats: reportData?.data || [],
        defaultDates,
        lastFetchedFilters: {
          yards: payload.yardIds,
          startDate: payload.startDate,
          endDate: payload.endDate,
        },
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchReportStats = createAsyncThunk(
  "home/fetchReportStats",
  async ({ yardIds, startDate, endDate }, { rejectWithValue }) => {
    try {
      const res = await getReportStats({ yardIds, startDate, endDate });
      return {
        reportStats: res.data,
        lastFetchedFilters: { yards: yardIds, startDate, endDate },
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchYardsForCompany = createAsyncThunk(
  "home/fetchYardsForCompany",
  async ({ companyId }, { rejectWithValue }) => {
    try {
      const res = await getYardsByCompanyId(companyId, "YARD");
      if (!res.status || !Array.isArray(res.data))
        throw new Error("Failed to fetch yards");

      const yardOptions = res.data.map((yard) => ({ label: yard.yard_name, value: yard.yard_id }));
      const allYardIds = res.data.map((yard) => yard.yard_id);

      return { yardOptions, allYardIds };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ── WAREHOUSE ─────────────────────────────────────────────────
// NOTE: not touched in this pass — still today-anchored, no survey-date logic.
export const fetchWarehouseHomeData = createAsyncThunk(
  "home/fetchWarehouseHomeData",
  async ({ companyId }, { rejectWithValue }) => {
    try {
      const res = await getYardsByCompanyId(companyId, "WAREHOUSE");
      if (!res.status || !Array.isArray(res.data))
        throw new Error("Failed to fetch warehouses");

      const warehouseOptions = res.data.map((yard) => ({
        label: yard.yard_name,
        value: yard.yard_id,
      }));
      const allWarehouseIds = res.data.map((yard) => yard.yard_id);

      let gateOptions = [];
      let allGateIds = [];
      if (allWarehouseIds.length > 0) {
        const gatesRes = await getGatesByYardIds(allWarehouseIds);
        if (gatesRes.status && Array.isArray(gatesRes.data)) {
          gateOptions = gatesRes.data.map((gate) => ({
            label: gate.gate_name,
            value: gate.gate_id,
            yard_id: gate.yard_id,
          }));
          allGateIds = gateOptions.map((g) => g.value);
        }
      }

      const defaultDates = getDefaultDates();
      const formattedStartDate = formatDateToISOString(defaultDates.startDate, true);
      const formattedEndDate = formatDateToISOString(defaultDates.endDate, false);

      let gateOverview = [];
      let cargoSummary = { total_cargo: 0, loaded: 0, unloaded: 0 };
      let throughput = [];

      if (allGateIds.length > 0) {
        const [overviewRes, summaryRes, throughputRes] = await Promise.all([
          getGateStatusOverview({
            gateIds: allGateIds,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          }),
          getWarehouseCargoSummary({
            gateIds: allGateIds,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          }),
          getHourlyThroughput({
            gateIds: allGateIds,
            startDate: formattedStartDate,
            endDate: formattedEndDate,
          }),
        ]);

        gateOverview = overviewRes.status ? overviewRes.data : [];
        cargoSummary = summaryRes.status ? summaryRes.data : cargoSummary;
        throughput = throughputRes.status ? throughputRes.data : [];
      }

      return {
        warehouseOptions,
        allWarehouseIds,
        gateOptions,
        allGateIds,
        defaultDates,
        gateOverview,
        cargoSummary,
        throughput,
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchWarehouseDashboardStats = createAsyncThunk(
  "home/fetchWarehouseDashboardStats",
  async ({ gateIds, startDate, endDate }, { rejectWithValue }) => {
    try {
      const [overviewRes, summaryRes, throughputRes] = await Promise.all([
        getGateStatusOverview({ gateIds, startDate, endDate }),
        getWarehouseCargoSummary({ gateIds, startDate, endDate }),
        getHourlyThroughput({ gateIds, startDate, endDate }),
      ]);

      return {
        gateOverview: overviewRes.status ? overviewRes.data : [],
        cargoSummary: summaryRes.status
          ? summaryRes.data
          : { total_cargo: 0, loaded: 0, unloaded: 0 },
        throughput: throughputRes.status ? throughputRes.data : [],
      };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

const homeSlice = createSlice({
  name: "home",
  initialState: {
    yard: {
      yardOptions: [],
      reportStats: [],
      lastFetchedFilters: null,
      loaded: false,
      isLoading: false,
    },
    warehouse: {
      warehouseOptions: [],
      gateOptions: [],
      gateOverview: [],
      cargoSummary: {
        total_cargo: 0,
        loaded: 0,
        unloaded: 0,
      },
      throughput: [],
      loaded: false,
      isLoading: false,
    },
    isYardsLoading: false,
    yardOptions: [],
    error: null,
  },
  reducers: {
    resetHomeStats: (state) => {
      state.yard.reportStats = [];
      state.yard.lastFetchedFilters = null;
    },
    resetAllHomeData: (state) => {
      state.yard = {
        yardOptions: [],
        reportStats: [],
        lastFetchedFilters: null,
        loaded: false,
        isLoading: false,
      };
      state.warehouse = {
        warehouseOptions: [],
        gateOptions: [],
        gateOverview: [],
        cargoSummary: { total_cargo: 0, loaded: 0, unloaded: 0 },
        throughput: [],
        loaded: false,
        isLoading: false,
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // ── YARD ──
      .addCase(fetchYardHomeData.pending, (state) => {
        state.yard.isLoading = true;
        state.error = null;
      })
      .addCase(fetchYardHomeData.fulfilled, (state, action) => {
        state.yard.isLoading = false;
        state.yard.loaded = true;
        state.yard.yardOptions = action.payload.yardOptions;
        state.yard.reportStats = action.payload.reportStats;
        state.yard.lastFetchedFilters = action.payload.lastFetchedFilters;
      })
      .addCase(fetchYardHomeData.rejected, (state, action) => {
        state.yard.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchReportStats.pending, (state) => {
        state.yard.isLoading = true;
        state.error = null;
      })
      .addCase(fetchReportStats.fulfilled, (state, action) => {
        state.yard.isLoading = false;
        state.yard.reportStats = action.payload.reportStats;
        state.yard.lastFetchedFilters = action.payload.lastFetchedFilters;
      })
      .addCase(fetchReportStats.rejected, (state, action) => {
        state.yard.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchYardsForCompany.pending, (state) => {
        state.isYardsLoading = true;
      })
      .addCase(fetchYardsForCompany.fulfilled, (state, action) => {
        state.isYardsLoading = false;
        state.yardOptions = action.payload.yardOptions;
      })
      .addCase(fetchYardsForCompany.rejected, (state, action) => {
        state.isYardsLoading = false;
        state.error = action.payload;
      })

      // ── WAREHOUSE ──
      .addCase(fetchWarehouseHomeData.pending, (state) => {
        state.warehouse.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWarehouseHomeData.fulfilled, (state, action) => {
        state.warehouse.isLoading = false;
        state.warehouse.loaded = true;
        state.warehouse.warehouseOptions = action.payload.warehouseOptions;
        state.warehouse.gateOptions = action.payload.gateOptions;
        state.warehouse.gateOverview = action.payload.gateOverview;
        state.warehouse.cargoSummary = action.payload.cargoSummary;
        state.warehouse.throughput = action.payload.throughput;
      })
      .addCase(fetchWarehouseHomeData.rejected, (state, action) => {
        state.warehouse.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchWarehouseDashboardStats.pending, (state) => {
        state.warehouse.isLoading = true;
        state.error = null;
      })
      .addCase(fetchWarehouseDashboardStats.fulfilled, (state, action) => {
        state.warehouse.isLoading = false;
        state.warehouse.gateOverview = action.payload.gateOverview;
        state.warehouse.cargoSummary = action.payload.cargoSummary;
        state.warehouse.throughput = action.payload.throughput;
      })
      .addCase(fetchWarehouseDashboardStats.rejected, (state, action) => {
        state.warehouse.isLoading = false;
        state.error = action.payload;
      });
  },
});

export const { resetHomeStats, resetAllHomeData } = homeSlice.actions;
export default homeSlice.reducer;